import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Loader2 } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useData } from "@/context/DataContext";
import { KpiCard, Panel, PageHeader, ChartTip } from "@/components/Widgets";
import { cut, errMsg, fmt, fmtCr, http, sum, last } from "@/lib/api";
import { toast } from "sonner";

const SLIDERS = [
  { k: "income_esc", label: "Income escalation Δ", unit: "pp", min: -10, max: 10, step: 0.5, hint: "Added to each line's 3-yearly escalation %" },
  { k: "expense_esc", label: "Expense escalation Δ", unit: "pp", min: -10, max: 10, step: 0.5, hint: "Added to salary / opex escalation %" },
  { k: "unit_capex_pct", label: "Normal (unit) capex", unit: "%", min: -50, max: 50, step: 5, hint: "Scale unit capex up or down" },
  { k: "project_capex_pct", label: "Project capex", unit: "%", min: -50, max: 50, step: 5, hint: "Scale project capex (net of reimbursements)" },
  { k: "interest_bps", label: "Interest rate Δ", unit: "bps", min: -300, max: 300, step: 25, hint: "Applied to average loan balances" },
];
const BASE = { income_esc: 0, expense_esc: 0, unit_capex_pct: 0, project_capex_pct: 0, interest_bps: 0 };

const SliderRow = ({ s, value, onChange }) => (
  <div data-testid={`scenario-${s.k}`}>
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-slate-200">{s.label}</span>
      <span className={`num text-sm ${value > 0 ? "text-emerald-400" : value < 0 ? "text-rose-400" : "text-slate-400"}`} data-testid={`scenario-${s.k}-value`}>
        {value > 0 ? "+" : ""}{value} {s.unit}
      </span>
    </div>
    <Slider className="mt-3" min={s.min} max={s.max} step={s.step} value={[value]} onValueChange={([v]) => onChange(v)} data-testid={`scenario-${s.k}-slider`} />
    <div className="mt-1.5 text-[11px] text-slate-500">{s.hint}</div>
  </div>
);

export default function Scenarios() {
  const { data, horizon, filters } = useData();
  const ents = data.model.entities;
  const scope = filters.entity;
  const initSw = useMemo(() => Object.fromEntries(ents.map((e) => [e.code, !!e.switch])), [ents]);
  const [p, setP] = useState(BASE);
  const [sw, setSw] = useState(initSw);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const vid = data.version.id;

  useEffect(() => {
    setSw(initSw);
  }, [initSw]);

  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await http.post(`/versions/${vid}/scenario`, { ...p, switches: sw, scope });
        setRes(r.data);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [p, sw, vid, scope]);

  const years = cut(data.model.years, horizon);
  const se = scope && res?.entities.find((x) => x.code === scope);
  const b = (k) => cut(se && k === "ncf" ? se.ncf_base : res?.base[k], horizon);
  const sc = (k) => cut(se && k === "ncf" ? se.ncf_scenario : res?.scenario[k], horizon);
  const chart = years.map((y, i) => ({ y, base_ncf: b("ncf")[i], scn_ncf: sc("ncf")[i], base_close: b("closing")[i], scn_close: sc("closing")[i] }));
  const dNcf = sum(sc("ncf")) - sum(b("ncf"));
  const dClose = last(sc("closing")) - last(b("closing"));
  return (
    <div data-testid="scenarios-page">
      <PageHeader kicker={se ? "Drivers applied to the selected entity only" : "Recalculated from workbook lines"} title={se ? `What-if Scenarios - ${se.short}` : "What-if Scenarios"}>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-400" />}
        <Button variant="outline" size="sm" className="border-slate-700" data-testid="scenario-reset-button" onClick={() => { setP(BASE); setSw(initSw); }}>
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Reset to base
        </Button>
      </PageHeader>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-4">
          <Panel title="Drivers" testid="scenario-drivers">
            <div className="space-y-6">
              {SLIDERS.map((s) => <SliderRow key={s.k} s={s} value={p[s.k]} onChange={(v) => setP((o) => ({ ...o, [s.k]: v }))} />)}
            </div>
          </Panel>
          <Panel title="Entity switches" subtitle="Include / exclude entity NCF from the group" testid="scenario-switches">
            <div className="space-y-2.5">
              {ents.map((e) => (
                <label key={e.code} className="flex items-center justify-between text-sm text-slate-300">
                  <span>{e.name}{!e.switch && <span className="ml-1 text-[11px] text-slate-500">(off in base)</span>}</span>
                  <Switch checked={!!sw[e.code]} onCheckedChange={(v) => setSw((o) => ({ ...o, [e.code]: v }))} data-testid={`scenario-switch-${e.code}`} />
                </label>
              ))}
            </div>
          </Panel>
        </div>
        <div className="space-y-6 xl:col-span-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard testid="scenario-kpi-ncf" label={se ? `${se.short} scenario NCF (horizon)` : "Scenario NCF (horizon)"} value={fmtCr(sum(sc("ncf")))} sub={`Base ${fmtCr(sum(b("ncf")))}`} tone={sum(sc("ncf")) >= 0 ? "pos" : "neg"} />
            <KpiCard testid="scenario-kpi-delta" label="Δ vs base (horizon)" value={`${dNcf >= 0 ? "+" : ""}${fmtCr(dNcf)}`} tone={dNcf >= 0 ? "pos" : "neg"} />
            <KpiCard testid="scenario-kpi-closing" label={`${se ? "Group closing funds" : "Closing funds"} FY ${last(years)}`} value={fmtCr(last(sc("closing")))} sub={`Δ ${fmtCr(dClose)} · low ${fmtCr(Math.min(...(sc("closing").length ? sc("closing") : [0])))}`} tone={last(sc("closing")) >= 0 ? "pos" : "neg"} />
          </div>
          <Panel title="Base vs scenario" subtitle="Bars: group NCF · lines: closing funds" testid="scenario-chart">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chart}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="y" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} tickFormatter={fmt} width={60} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#475569" />
                <Bar dataKey="base_ncf" name={se ? "Base entity NCF" : "Base NCF"} fill="#475569" radius={[3, 3, 0, 0]} />
                <Bar dataKey="scn_ncf" name={se ? "Scenario entity NCF" : "Scenario NCF"} fill="#F59E0B" radius={[3, 3, 0, 0]} />
                <Line dataKey="base_close" name={se ? "Base group closing" : "Base closing"} stroke="#94A3B8" strokeDasharray="5 4" strokeWidth={2} dot={false} />
                <Line dataKey="scn_close" name={se ? "Scenario group closing" : "Scenario closing"} stroke="#10B981" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Panel>
          <EntityImpact res={res} />
          <p className="text-[11px] leading-relaxed text-slate-500">
            Method: escalation deltas are applied only at the years where the workbook line escalates by its own 3-yearly rate (detected from the values), so flat years stay flat.
            Loan drawdowns and repayments are held at workbook values; central funding and other corporate NCF rows are unchanged.
          </p>
        </div>
      </div>
    </div>
  );
}

const EntityImpact = ({ res }) => {
  const { horizon, filters } = useData();
  if (!res) return null;
  return (
    <Panel title="Impact by entity" subtitle="Cumulative NCF over horizon" testid="scenario-entity-impact">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
          <th className="py-2">Entity</th><th className="py-2 text-right">Base</th><th className="py-2 text-right">Scenario</th><th className="py-2 text-right">Δ</th>
        </tr></thead>
        <tbody>
          {res.entities.filter((e) => !filters.entity || e.code === filters.entity).map((e) => {
            const b = sum(cut(e.ncf_base, horizon));
            const s = sum(cut(e.ncf_scenario, horizon));
            return (
              <tr key={e.code} className="border-t border-slate-800" data-testid={`scenario-impact-${e.code}`}>
                <td className="py-2 text-slate-200">{e.short}</td>
                <td className="num py-2 text-right">{fmt(b)}</td>
                <td className="num py-2 text-right">{fmt(s)}</td>
                <td className={`num py-2 text-right ${s - b >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmt(s - b)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
};
